// Cost Plus Forecast — the VoltaGrid jobs' Bid Forecast, on two bases.
//
// A cost-plus job has no contract value to forecast profit against: revenue
// is whatever the cost bills for. So the Bid Forecast's grid gets a second
// line under every LEMSO class — its billable side: the Day Zero's Billable
// Cost column as the budget and a Billable EAC — and the Contract Amount row
// becomes Revenue (Billable) with a real EAC. Job Profit is then revenue less
// cost, column by column, as on the Bid Forecast.
//
// Billable EAC is built, never ratioed:
//   invoiced      — what the lines sent to the customer billed
//   not invoiced  — every other billable line at the Billing tab's own price
//                   (rate sheet hours, markups, contract rules, fuel), plus
//                   an estimate for the ST / OT lines the sheet can't price
//                   yet, which bill $0 there until their class is mapped
//   committed     — each open PO line priced the way the Billing tab will
//                   price it once it posts: its description and supplier
//                   through the contract rules, then the job's markup
//   ETC           — the forecast's cost to go by cost code and cost type,
//                   priced the same way; what bills on hours rather than
//                   cost (ST / OT labor, fleet and rental equipment) at the
//                   job's own rate sheet billing per dollar of cost so far
//
// Billable to date comes in as the Billing tab's priced lines (billingPricing
// via useBillingRevenue — `t.bill` + the fuel surcharge per line); the cost
// to go as helpers/jobBudgetEac's plan (open POs + ETC tabs + burn tool weeks
// + contingency, the Overview's rules) with the open POs split back out as
// Committed; and the cost to go is priced by billingPricing's priceToGo.
// This file only buckets and foots.

import moment from 'moment';
import { pNum } from '../db/company';
import { bucketOf, priceToGo, NEVER_BILLED_CT_PREFIX } from '../components/billingPricing';
import { fuelLineAmt } from '../components/billingFuel';
import { revenueBudgetRows } from './revenueBudget';

export const CP_CLASSES = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other'];
export const CP_LABELS = { Labor: 'Labor', Equipment: 'Equipment', Materials: 'Materials', Subcontractors: 'Subcontractors', Other: 'Other Expenses' };

// Straight time and overtime bill hours × the rate sheet; cost alone has no
// hours, so their cost to go (and their unmapped lines to date) bill at what
// the job's own ST / OT has billed per dollar of wage so far.
const LABOR_ST = '51100';
const LABOR_OT = '51200';
const LABOR_PD = '51500';
// Fleet and rental equipment bill hours × the sheet's tier rates (rentals off
// HeavyJob usage), not cost — once a cost type has this much cost behind it,
// its cost to go bills at what it has billed per dollar so far; short of it,
// at the job's markup.
const EQUIP_RATED = ['52100', '52200'];
export const EQUIP_HIST_MIN = 10000;
// ETC the forecast carries with no cost type prices as the class's usual one.
const CLASS_CT = { Labor: LABOR_ST, Equipment: '52200', Materials: '53100', Subcontractors: '54200', Other: '57900' };
// Weeks of billable that set the run rate on the not-to-exceed card.
export const RUN_RATE_WEEKS = 4;

const CONTINGENCY_CODE = '101101';
// Summary stages that haven't gone to the customer (billing.js's Not Sent
// filter, plus Void).
const NOT_SENT = ['Draft', 'Kicked Back', 'Void'];
const OH_COST_TYPE = '60900';

const str = (v) => (v === null || v === undefined ? '' : String(v).trim());
const isRev = (ct) => ct.startsWith('4');
const isCost = (ct) => ct.startsWith('5');
const skipCode = (cc) => cc === '1000' || cc === '990000';
const ratioOf = (num, den) => (den > 0 ? num / den : null);
const pctOf = (num, den) => (den ? num / den : null);

// Has summary `no` gone to the customer? Off the Billing tab's summaries
// ({ no, status }); a number that isn't among them counts as sent.
export const sentTestOf = (invoices) => {
    const unsent = new Set((Array.isArray(invoices) ? invoices : [])
        .filter((i) => i && NOT_SENT.includes(i.status)).map((i) => str(i.no)));
    return (inv) => !!inv && !unsent.has(str(inv));
};

// Where the Billing tab's sent summaries and JDE's invoices disagree — the
// invoiced revenue is only as right as the summaries. A summary is FTMS's
// number (F00294114), JDE's the same digits (294114). `missing`: billed in
// JDE with no sent summary; `differ`: a sent summary whose FTMS total isn't
// what JDE has on that invoice (reissued, voided). Under a dollar is noise.
export const invoiceGapsOf = (invoices, billings) => {
    const num = (v) => str(v).replace(/\D/g, '').replace(/^0+/, '');
    const jde = {};
    (Array.isArray(billings) ? billings : []).forEach((b) => {
        const k = num(b && b.Invoice_Number);
        if (k) jde[k] = (jde[k] || 0) + pNum(b.Invoice_Amount || 0);
    });
    const sent = (Array.isArray(invoices) ? invoices : []).filter((i) => i && !NOT_SENT.includes(i.status));
    const onBilling = new Set(sent.map((i) => num(i.no)));
    const missing = Object.keys(jde).filter((k) => Math.abs(jde[k]) >= 1 && !onBilling.has(k)).sort().map((k) => ({ no: k, jde: jde[k] }));
    const differ = sent
        .filter((i) => i.ftms && i.ftms.total != null && i.ftms.total !== '')
        .map((i) => ({ no: str(i.no), billing: pNum(i.ftms.total), jde: jde[num(i.no)] || 0 }))
        .filter((d) => Math.abs(d.billing - d.jde) >= 1);
    return { missing, differ, jdeBilled: Object.values(jde).reduce((a, v) => a + v, 0) };
};

// Latest save wins per (job, tab, field, row) on the version — the
// Overview's dedupe.
const latestRows = (userData, version, job) => {
    const latest = {};
    (Array.isArray(userData) ? userData : []).forEach((d) => {
        if (!d || String(d.Version_GUID) !== String(version)) return;
        if (job != null && job !== '' && str(d.BU_ID) !== str(job)) return;
        const key = `${d.BU_ID}_${d.Forecast_Tab}_${d.Forecast_Field_Name}_${d.Forecast_Row_ID}`;
        const at = Math.max(
            new Date(d.DSS_Created_Date || d.DSS_Created_Datetime || 0).getTime() || 0,
            new Date(d.DSS_Updated_Datetime || 0).getTime() || 0,
        );
        if (!latest[key] || at >= latest[key].at) latest[key] = { at, d };
    });
    return Object.values(latest).map((x) => x.d);
};

// A Bid Forecast field on the version (Forecast tab: nteAmount, notes,
// physicalComplete …) — the same row both tabs read and write.
export const forecastFieldOf = (userData, version, job, field) => {
    const row = latestRows(userData, version, job).find((d) => d.Forecast_Tab === 'Forecast' && d.Forecast_Field_Name === field);
    return row ? row.Forecast_Value : undefined;
};
export const nteAmountOf = (userData, version, job) => pNum(forecastFieldOf(userData, version, job, 'nteAmount') || 0);

// How the cost to go bills, kept by method ({ how, cost, bill }) so a row can
// say what its Billable EAC is made of; biggest first.
const addHow = (list, how, cost, bill) => {
    const h = list.find((x) => x.how === how);
    if (h) { h.cost += cost; h.bill += bill; } else list.push({ how, cost, bill });
};
const mergeHows = (rows) => {
    const out = [];
    rows.forEach((r) => (r.toGoHow || []).forEach((h) => addHow(out, h.how, h.cost, h.bill)));
    return out.sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost));
};

// One class or one cost type, footed. `billCommitted` / `billEtc` (the cost
// to go, priced) and `noRateEst` come in already built — see costPlusModel.
//
// `jtdCost` is net of the not-billable lines (`nbCost`), which sit on the
// Non-Billable row instead; the to-date ratio is on all of it, for the hover.
// JTD splits three ways: invoiced (`sentCost`), not invoiced, and the
// JTD Cost Non-Billable column (`jtdNb`, only the Non-Billable row has any).
const finish = (row) => {
    const out = { ...row };
    const allCost = out.jtdCost + (out.nbCost || 0);
    out.jtdNb = out.jtdNb || 0;
    // The not-billable cost to date takes its budget with it to the
    // Non-Billable row, so a class on budget reads on budget (Aeron 10/28:
    // "we need all these EACs to match budget from the very top lines to
    // the details"). The bid column keeps the bid.
    out.curCostBudget = out.costBudget + out.coCost - (out.nbCost || 0);
    out.bidRatio = ratioOf(out.billBudget, out.costBudget);
    out.toDateRatio = out.priced ? ratioOf(out.jtdBill, allCost) : null;
    out.unbilled = out.jtdBill - out.invoiced;
    out.unsentCost = out.jtdCost - out.sentCost - out.jtdNb;
    // Not invoiced, priced: the billable lines not yet sent at their Billing
    // tab price, plus the estimate for the lines the sheet can't price yet.
    out.unsentBill = out.jtdBill - out.sentBill + (out.noRateEst || 0);
    out.costEac = out.jtdCost + out.committed + out.etc;
    out.costDeltaEac = out.costEac - out.curCostBudget;
    out.costDeltaBid = out.costEac - out.costBudget;
    const toGo = out.committed + out.etc;
    if (out.priced) {
        out.billToGo = out.billCommitted + out.billEtc;
        out.toGoRatio = toGo ? out.billToGo / toGo : null;
        out.billEac = out.sentBill + out.unsentBill + out.billToGo;
        out.margin = out.billEac - out.costEac;
        out.marginPct = pctOf(out.margin, out.billEac);
    } else {
        out.billCommitted = null; out.billEtc = null; out.billToGo = null; out.toGoRatio = null; out.billEac = null; out.margin = null; out.marginPct = null;
    }
    // Approved CO cost bills at the bid's ratio (the Overview's rule), or at
    // what the cost to go bills at when the bid has no price for it.
    out.coBill = out.coCost * (out.bidRatio != null ? out.bidRatio : out.toGoRatio != null ? out.toGoRatio : 1);
    out.curBillBudget = out.billBudget + out.coBill;
    out.billDeltaEac = out.billEac == null ? null : out.billEac - out.curBillBudget;
    out.billDeltaBid = out.billEac == null ? null : out.billEac - out.billBudget;
    return out;
};

// Straight time and overtime to date, split out: cost, hours and what the
// hours come to at the rate sheet (`sheet`; a line the sheet has no rate for
// counts at what it billed). The Labor card states these; the cost to go
// and the unmapped lines bill off them.
const laborPartsOf = (txns, fuelCtx) => {
    const part = () => ({ n: 0, hrs: 0, cost: 0, bill: 0, sheet: 0 });
    const out = { st: part(), ot: part(), perDiem: part(), other: part(), noRate: part(), nb: part() };
    const byClass = {};
    (Array.isArray(txns) ? txns : []).forEach((t) => {
        const ct = str(t && t.ct);
        if (!t || bucketOf(ct) !== 'Labor') return;
        const cost = pNum(t.cost);
        if (t.nb) { out.nb.n += 1; out.nb.cost += cost; return; }
        const hrs = pNum(t.hrs);
        const amt = pNum(t.bill) + fuelLineAmt(t, fuelCtx || {});
        const isRate = ct === LABOR_ST || ct === LABOR_OT;
        const sheet = isRate && pNum(t.rateAmt) > 0 ? hrs * pNum(t.rateAmt) : amt;
        const p = t.noRate ? out.noRate : ct === LABOR_ST ? out.st : ct === LABOR_OT ? out.ot : ct === LABOR_PD ? out.perDiem : out.other;
        p.n += 1; p.cost += cost; p.bill += amt; p.sheet += sheet;
        if (isRate) p.hrs += hrs;
        if (!isRate || t.noRate) return;
        const name = str(t.ba) || str(t.jde) || 'Unclassified';
        const c = byClass[name] || (byClass[name] = { name, stHrs: 0, stSheet: 0, otHrs: 0, otSheet: 0, bill: 0 });
        if (ct === LABOR_ST) { c.stHrs += hrs; c.stSheet += sheet; } else { c.otHrs += hrs; c.otSheet += sheet; }
        c.bill += amt;
    });
    out.byClass = byClass;
    return out;
};

// What a dollar of ST / OT wage bills for. ST cost is hours × wage and
// labor bills the person's actual wage × the multiplier (billingPricing's
// laborRateFor — never the sheet's target wage), so the job's own billed ÷
// cost on priced lines is the multiplier on the wages it actually pays
// (1.633 on lines priced since the actual-wage rule; the summaries frozen
// at a target-wage invoice before it still carry what they invoiced). With
// no labor priced yet, the rate sheet's own multiplier (OT: × its OT factor
// ÷ the 1.5 payroll pays). `otFactor` is what an OT hour bills over an ST
// hour — the sheet's OT factor, else the job's own OT ÷ ST rate an hour —
// for the burn tool's hours past 40 a week (see costPlusModel).
const laborRatesOf = (parts, rateCfg) => {
    const { st, ot } = parts;
    // Number, not pNum: the multiplier has three decimals (1.633).
    const m = Number(rateCfg && rateCfg.m) || 0;
    const otf = Number(rateCfg && rateCfg.otf) || 0;
    const stRatio = st.cost > 0 && st.sheet > 0 ? st.sheet / st.cost : null;
    const otRatio = ot.cost > 0 && ot.sheet > 0 ? ot.sheet / ot.cost : null;
    const stHr = st.hrs ? st.sheet / st.hrs : null;
    const otHr = ot.hrs ? ot.sheet / ot.hrs : null;
    const pick = (ratio, how) => (ratio != null ? { ratio, how } : null);
    const stCfg = m ? { ratio: m, how: `Rate sheet multiplier ${ratioText(m)} on wage — no straight time priced yet` } : null;
    const otCfg = m && otf ? { ratio: (m * otf) / 1.5, how: `Rate sheet OT (${ratioText(m)} × ${otf} ÷ 1.5 on OT wage) — no overtime priced yet` } : null;
    return {
        otShare: st.hrs + ot.hrs > 0 ? ot.hrs / (st.hrs + ot.hrs) : 0,
        otFactor: otf || (stHr && otHr ? otHr / stHr : null),
        [LABOR_ST]: pick(stRatio, `Rate sheet straight time, ${ratioText(stRatio)} of wage so far`) || stCfg,
        [LABOR_OT]: pick(otRatio, `Rate sheet overtime, ${ratioText(otRatio)} of OT wage so far`) || otCfg,
        // An unmapped line to date bills at its own cost type's rate.
        noRate: { [LABOR_ST]: stRatio != null ? stRatio : (stCfg && stCfg.ratio), [LABOR_OT]: otRatio != null ? otRatio : (otCfg && otCfg.ratio) },
    };
};

// How the Labor class's billable EAC is built, for the card under the grid.
// Straight time and overtime bill hours × the rate sheet rate of the class
// each person bills as; per diem and the rest of the billable labor lines
// bill at cost; lines the sheet can't price yet bill $0 on the Billing tab
// and are estimated here; the cost to go is priced cost type by cost type.
//
// ST / OT are stated as JDE hours × the rate sheet rate (`sheet`), not
// billed ÷ hours: an invoiced line carries the dollars its signed field
// ticket billed, and the tickets split ST from OT (and count hours) their
// own way, not JDE payroll's weekly way — on 54026070 that moved $155k
// between ST and OT while the total moved $11k. That difference is its own
// line (`adj` = billed − sheet), so the rates read as the sheet has them
// (Craftsman - A = $45 × 1.633 = $73.49) and the lines still foot to the
// billable to date. `neverBilled` is the labor cost that is never a Billing
// line (burden, payroll taxes, truck and rig pay), which is why cost ÷ hours
// runs above what the hours alone cost.
const laborBillOf = (parts, labor) => {
    const { byClass, ...rest } = parts;
    const out = { ...rest };
    const rateOf = (amt, hrs) => (hrs ? amt / hrs : null);
    ['st', 'ot'].forEach((k) => { out[k].rate = rateOf(out[k].sheet, out[k].hrs); });
    out.rated = { hrs: out.st.hrs + out.ot.hrs, sheet: out.st.sheet + out.ot.sheet, bill: out.st.bill + out.ot.bill };
    out.rated.rate = rateOf(out.rated.sheet, out.rated.hrs);
    // Invoiced ST / OT against the same hours at the rate sheet.
    out.adj = out.rated.bill - out.rated.sheet;
    // All hours worked — priced or not — for the all-in rates.
    out.hrs = out.rated.hrs + out.noRate.hrs;
    out.jtdBill = labor.jtdBill;
    // All of the class's cost, the lines held not billable included — the
    // grid's Labor row is net of them.
    out.jtdCost = labor.jtdCost + labor.nbCost;
    out.billPerHr = rateOf(labor.jtdBill, out.hrs);
    out.costPerHr = rateOf(out.jtdCost, out.hrs);
    const onBilling = ['st', 'ot', 'perDiem', 'other', 'noRate', 'nb'].reduce((a, k) => a + out[k].cost, 0);
    out.neverBilled = out.jtdCost - onBilling;
    // The unmapped ST / OT lines, at their cost type's rate.
    out.noRateEst = labor.noRateEst || 0;
    out.committed = labor.committed;
    out.etc = labor.etc;
    out.toGo = labor.committed + labor.etc;
    out.toGoRatio = labor.toGoRatio;
    out.billToGo = labor.billToGo;
    // The cost to go by cost type, each with how it bills. ST / OT also as
    // hours: the cost to go at the to-date wage per hour, and what each hour
    // bills.
    const wage = { [LABOR_ST]: rateOf(out.st.cost, out.st.hrs), [LABOR_OT]: rateOf(out.ot.cost, out.ot.hrs) };
    out.toGoRows = [...labor.subRows, ...(labor.noCt ? [labor.noCt] : [])]
        .filter((s) => Math.round(s.committed + s.etc))
        .map((s) => {
            const cost = s.committed + s.etc;
            // The burn tool's own hours where it has them; the rest of the
            // cost at the wage paid so far (labor ETC with no cost type is
            // straight time).
            const w = wage[s.ct || CLASS_CT.Labor];
            const rest = w ? (cost - (s.etcHrsCost || 0)) / w : 0;
            const hrs = s.etcHrs || w ? (s.etcHrs || 0) + rest : null;
            return { ct: s.ct, label: s.label, cost, bill: s.billToGo, how: s.toGoHow, hrs, otHrs: s.etcOtHrs || 0, rate: hrs ? s.billToGo / hrs : null };
        })
        .sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost));
    out.toGoHrs = out.toGoRows.reduce((a, r) => a + (r.hrs || 0), 0) || null;
    out.toGoOtHrs = out.toGoRows.reduce((a, r) => a + (r.otHrs || 0), 0);
    out.billEac = labor.billEac;
    out.byClass = Object.values(byClass).map((c) => ({
        ...c, stRate: rateOf(c.stSheet, c.stHrs), otRate: rateOf(c.otSheet, c.otHrs), hrs: c.stHrs + c.otHrs, sheet: c.stSheet + c.otSheet,
    })).sort((a, b) => b.sheet - a.sheet || a.name.localeCompare(b.name));
    return out;
};

const SUM_KEYS = ['costBudget', 'coCost', 'billBudget', 'jtdCost', 'jtdHrs', 'jtdBill', 'invoiced', 'sentCost', 'sentBill', 'nbCost', 'nbSent', 'noRate', 'noRateEst', 'committed', 'etc', 'etcHrs', 'etcOtHrs', 'etcHrsCost', 'billCommitted', 'billEtc'];
const blank = (extra) => SUM_KEYS.reduce((a, k) => { a[k] = 0; return a; }, { ...extra });
const sumOf = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
const rollup = (rows, keys, extra) => keys.reduce((a, k) => { a[k] = sumOf(rows, k); return a; }, { ...extra });

// The model.
//   userData / version / job — the forecast rows (Day Zero, Revenue Budget,
//     approved Change Orders) on the version
//   plan — jobPlanByCode(): cost to go per code per class; null while loading
//   openCommitments — the job's open POs (jobOpenCommitmentsAtom), the
//     Committed column; the plan's remaining less these is the ETC
//   jobCostDetail — the job's cost history (cost to date, every 5xxxx line)
//   txns / fuelCtx — useBillingRevenue's priced lines; `ready` false until
//     they are in, so the billable side stays blank rather than $0
//   invoices — the Billing tab's summaries ({ no, status }): a line's cost
//     counts as invoiced (`sentCost`, and what it billed `sentBill`) once
//     its summary has gone to the customer, and the burden of its payroll
//     week with it; a Draft, Kicked Back or Void one hasn't. Without the
//     list, any line on a summary counts. A
//     not-billable line a sent summary closed out (`nbInv`) is invoiced
//     not-billable cost, and a header's `ftms.noLine` is billed revenue
//     with no line behind it
//   priceCtx — useBillingRevenue's pricing context: what billingPricing's
//     priceToGo prices the open POs and the ETC with (settings, contract
//     rules, rate sheet maps)
//   rateCfg — the rate sheet's { m, otf }: what ST / OT to go bills at
//     before any labor has priced
//   ohPercent — the job's overhead % (of revenue, as the Bid Forecast has it)
//   versionStart — the version's start; billable before the prior month's
//     end is "earned thru prev period", as on the Bid Forecast
//   nte — the PO's not-to-exceed; jdeBilled — JDE's invoiced total
//   today — for the run rate's window and the fuel index the cost to go
//     prices at (tests pin it)
export function costPlusModel({ userData, version, job, plan, openCommitments, jobCostDetail, txns, fuelCtx, invoices, priceCtx, rateCfg, ready = true, ohPercent = 0, versionStart, nte = 0, jdeBilled = 0, today } = {}) {
    const cts = {};
    const ctOf = (ct) => cts[ct] || (cts[ct] = blank({ ct, cls: bucketOf(ct), priced: ready }));

    // Budget: the Day Zero cost lines and the approved COs' cost lines by
    // cost type; contingency (101101) and overhead (60900) on their own;
    // CO revenue (4xxxx) for the note.
    let coRevenue = 0;
    let dayZeroRevenue = 0;
    let contingencyBudget = 0;
    let ohBudget = 0;
    latestRows(userData, version, job).forEach((d) => {
        const ct = str(d.Cost_Type);
        const cc = str(d.Cost_Code);
        if (d.Forecast_Tab === 'Day Zero Budget') {
            if (isRev(ct)) { dayZeroRevenue += pNum(d.Value_Amount || 0); return; }
            if (ct === OH_COST_TYPE) { ohBudget += pNum(d.Value_Amount || 0); return; }
            if (!isCost(ct) || skipCode(cc)) return;
            if (cc === CONTINGENCY_CODE) { contingencyBudget += pNum(d.Value_Amount || 0); return; }
            ctOf(ct).costBudget += pNum(d.Value_Amount || 0);
        } else if (d.Forecast_Tab === 'Change Order') {
            if (String(d.Status || d.status || '').toLowerCase() !== 'approved') return;
            if (isRev(ct)) { coRevenue += pNum(d.Value_Amount || 0); return; }
            if (!isCost(ct)) return;
            ctOf(ct).coCost += pNum(d.Value_Amount || 0);
        }
    });
    const revLines = revenueBudgetRows(userData, version);
    revLines.forEach((l) => { if (l.ct && l.cc !== CONTINGENCY_CODE) ctOf(l.ct).billBudget += l.amount; });
    const contingencyBillBudget = revLines.filter((l) => l.cc === CONTINGENCY_CODE).reduce((a, l) => a + l.amount, 0);
    const hasRevBudget = revLines.length > 0;

    // Cost to date: every cost line, billable or not — the ratio is what
    // the whole class bills for, burden and held lines included. The held
    // lines move to Non-Billable once the billing lines are in (below);
    // burden, never a Billing line, stays. Overhead (60900) is its own row.
    // The never-billed lines (burden, payroll taxes, BYOD, truck and rig
    // pay) are kept by GL date to split invoiced / not invoiced below.
    let jtdOh = 0;
    const neverLines = [];
    (Array.isArray(jobCostDetail) ? jobCostDetail : []).forEach((r) => {
        const ct = str(r && r.Cost_Type_Code);
        if (ct === OH_COST_TYPE) { jtdOh += pNum(r.Job_Cost || 0); return; }
        if (!isCost(ct)) return;
        const row = ctOf(ct);
        row.jtdCost += pNum(r.Job_Cost || 0);
        if (row.cls === 'Labor') row.jtdHrs += pNum(r.Total_Hours || 0);
        if (NEVER_BILLED_CT_PREFIX.some((p) => ct.startsWith(p))) {
            neverLines.push({ ct, cost: pNum(r.Job_Cost || 0), gl: r.GL_Date ? moment.utc(r.GL_Date).format('YYYY-MM-DD') : '' });
        }
    });

    // Committed: the open POs by cost type, the plan's own filter. Each line
    // keeps what the contract rules read (description, supplier) so it can
    // be priced as the Billing tab will price it once it posts.
    const openLines = [];
    (Array.isArray(openCommitments) ? openCommitments : []).forEach((c) => {
        const cc = str(c && c.Phase_Code);
        const ct = str(c && c.Cost_Type_Code);
        if (!cc || skipCode(cc) || !isCost(ct)) return;
        const cost = pNum(c.PO_True_Open_Amount || 0);
        ctOf(ct).committed += cost;
        if (!cost) return;
        openLines.push({
            side: 'committed', ct, cc, cost,
            ds: [c.PO_Line_Description, c.PO_Line_Description_2].map(str).filter(Boolean).join(' '),
            sup: str(c.Supplier_AB_Name || c.Supplier_Name), po: str(c.PO_Number), pol: str(c.PO_Line_Number),
            tier: str(c.Equipment_Classification_Tier_Name || c.Rental_Equipment_Classification_Tier_Name),
        });
    });

    // Billable to date: the priced lines, fuel included, invoiced split
    // out; not-billable cost held aside. Weekly billable for the run rate,
    // and the share earned before the prior month's end.
    const weeks = {};
    let billPrevPeriod = 0;
    const isSent = sentTestOf(invoices);
    // Dollars a sent summary billed with no JDE line to carry them (its
    // header's `ftms.noLine` — P-card vans, a rental whose month is dated in
    // the period before) are invoiced revenue all the same.
    const noLineSent = (Array.isArray(invoices) ? invoices : [])
        .filter((i) => i && !NOT_SENT.includes(i.status))
        .reduce((a, i) => a + pNum(i.ftms && i.ftms.noLine), 0);
    const prevPeriodEnd = versionStart ? moment(versionStart).subtract(1, 'month').endOf('month') : null;
    // Each sent summary's no-line dollars count as earned by its period's end.
    const noLinePrev = prevPeriodEnd ? (Array.isArray(invoices) ? invoices : [])
        .filter((i) => i && !NOT_SENT.includes(i.status))
        .filter((i) => {
            const end = moment(str(i.ftms && i.ftms.to) || str(i.date), 'YYYY-MM-DD', true);
            return end.isValid() && end.isBefore(prevPeriodEnd);
        })
        .reduce((a, i) => a + pNum(i.ftms && i.ftms.noLine), 0) : 0;
    // Straight time and overtime wages by GL date (payroll posts a week on
    // one date), and how much of them went out on an invoice.
    const wageGl = {};
    if (ready) {
        (Array.isArray(txns) ? txns : []).forEach((t) => {
            if (!t) return;
            const row = ctOf(str(t.ct));
            // A rental voucher whose unit bills on HeavyJob hours is off the
            // invoice only because its usage lines bill instead — its cost
            // is billable and stays in Equipment.
            if (t.nb && t.ruUnit) return;
            // A not-billable line a sent summary closed out at $0 (`nbInv`)
            // has been invoiced too — as not billable.
            if (t.nb) {
                row.nbCost += pNum(t.cost);
                if (isSent(t.nbInv)) row.nbSent += pNum(t.cost);
                return;
            }
            if (t.noRate) row.noRate += 1;
            const amt = pNum(t.bill) + fuelLineAmt(t, fuelCtx || {});
            if (str(t.ct) === LABOR_ST || str(t.ct) === LABOR_OT) {
                const w = wageGl[str(t.gl)] || (wageGl[str(t.gl)] = { all: 0, sent: 0 });
                w.all += pNum(t.cost);
                if (isSent(t.inv)) w.sent += pNum(t.cost);
            }
            if (isSent(t.inv)) { row.sentCost += pNum(t.cost); row.sentBill += amt; }
            row.jtdBill += amt;
            if (t.inv) row.invoiced += amt;
            const day = t.date ? moment(t.date, 'YYYY-MM-DD', true) : null;
            if (day && day.isValid()) {
                if (prevPeriodEnd && day.isBefore(prevPeriodEnd)) billPrevPeriod += amt;
                const k = day.startOf('isoWeek').format('YYYY-MM-DD');
                weeks[k] = (weeks[k] || 0) + amt;
            }
        });
        // Not-billable cost comes out of its class and onto the Non-Billable
        // row. The Billing tab prices the same cost history, so it nets
        // line for line; to the cent, so a cost type that was all
        // not billable lands on 0.
        Object.values(cts).forEach((row) => {
            if (row.nbCost) row.jtdCost = Math.round((row.jtdCost - row.nbCost) * 100) / 100;
        });
        // Burden, payroll taxes, BYOD, truck and rig pay are never a Billing
        // line, but they are the cost of the wages they were paid with, and
        // the labor rate bills for them. So each follows its payroll week:
        // invoiced in the share of that GL date's ST / OT wages that went out
        // on an invoice — a date with no wages, the job's share as a whole.
        // Otherwise the invoiced column's profit carries none of the burden
        // and the not-invoiced column carries all of it.
        const wageAll = Object.values(wageGl).reduce((a, w) => ({ all: a.all + w.all, sent: a.sent + w.sent }), { all: 0, sent: 0 });
        const shareOf = (w) => (w && w.all > 0 ? Math.min(1, Math.max(0, w.sent / w.all)) : null);
        const jobShare = shareOf(wageAll) || 0;
        neverLines.forEach((l) => {
            const s = shareOf(wageGl[l.gl]);
            ctOf(l.ct).sentCost += l.cost * (s != null ? s : jobShare);
        });
    }

    // Cost to go by class, off the plan: the remaining less the open POs is
    // the ETC. Contingency (code 101101) is its own row.
    const planReady = !!(plan && plan.codes);
    const remainingOf = (cls, code) => (planReady
        ? Object.entries(plan.codes).reduce((a, [cc, n]) => a + (code ? (cc === code ? 1 : 0) : (cc === CONTINGENCY_CODE ? 0 : 1)) * pNum((n.remaining || {})[cls] || 0), 0)
        : 0);

    // ETC by cost code and cost type, off the plan's `types` (the contingency
    // aside). A type filed under another class than its cost type's, or
    // carrying no cost type at all, stays with its class as "no cost type".
    const etcLines = [];
    if (planReady) {
        Object.entries(plan.codes).forEach(([cc, n]) => {
            if (cc === CONTINGENCY_CODE) return;
            Object.values((n && n.types) || {}).forEach((t) => {
                const ct = str(t && t.ct);
                const cost = pNum(t && t.etc);
                if (!cost || !ct || bucketOf(ct) !== t.cls) return;
                const row = ctOf(ct);
                row.etc += cost;
                // The burn tool's labor hours, and those past 40 a week.
                const hrs = pNum(t.hrs);
                const otHrs = pNum(t.otHrs);
                row.etcHrs += hrs;
                row.etcOtHrs += otHrs;
                if (hrs) row.etcHrsCost += pNum(t.hrsCost);
                etcLines.push({ side: 'etc', ct, cc, cost, hrs, otHrs, hrsCost: pNum(t.hrsCost) });
            });
        });
    }

    const allCts = Object.values(cts);
    const jobRow = rollup(allCts, SUM_KEYS, { priced: ready });
    const jobAllCost = jobRow.jtdCost + jobRow.nbCost;
    const jobBid = ratioOf(jobRow.billBudget, jobRow.costBudget);
    const jobToDate = ready ? ratioOf(jobRow.jtdBill, jobAllCost) : null;

    // Per class, the rest of its ETC — the class's remaining less its open
    // POs and its cost types' ETC — priced as the class's usual cost type.
    const noCts = {};
    CP_CLASSES.forEach((cls) => {
        const subs = allCts.filter((c) => c.cls === cls);
        const etc = planReady ? remainingOf(cls) - sumOf(subs, 'committed') - sumOf(subs, 'etc') : 0;
        if (Math.abs(etc) < 0.005) return;
        noCts[cls] = { ...blank({ key: `${cls}|noct`, ct: '', cls, label: 'No cost type', priced: ready }), etc };
        etcLines.push({ side: 'etc', ct: CLASS_CT[cls], cc: '', cost: etc, noCt: cls });
    });

    // The cost to go, priced. Every line goes through the Billing tab's rules
    // first (priceToGo): a cost type, cost code or item the contract keeps off
    // the invoice bills nothing, whatever it is. What bills on hours rather
    // than cost then prices off the job's own billing — ST / OT at the rate
    // sheet per dollar of wage (laborRatesOf), fleet and rental equipment at
    // what it has billed per dollar of cost once EQUIP_HIST_MIN has posted;
    // the rest bills as the rules price it (markup, at cost, pass-through,
    // fuel surcharge).
    //
    // The burn tool prices every labor hour at straight wage, a 60-hour week
    // included; the hours past 40 bill at the sheet's OT rate, so straight
    // time to go from it carries that lift — unless the forecast has
    // overtime (51200) of its own, which prices on its own.
    const laborParts = ready ? laborPartsOf(txns, fuelCtx) : null;
    const planHasOt = ((cts[LABOR_OT] && cts[LABOR_OT].etc) || 0) + ((cts[LABOR_OT] && cts[LABOR_OT].committed) || 0) > 0;
    const laborRates = ready ? laborRatesOf(laborParts, rateCfg) : null;
    const otLift = ready && !planHasOt && laborRates.otFactor > 1 ? laborRates.otFactor : null;
    const rateFor = (ct) => {
        if (ct === LABOR_ST || ct === LABOR_OT) return laborRates[ct];
        const r = EQUIP_RATED.includes(ct) ? cts[ct] : null;
        const all = r ? r.jtdCost + r.nbCost : 0;
        if (r && all >= EQUIP_HIST_MIN && r.jtdBill) {
            return { ratio: r.jtdBill / all, how: `${ct === '52100' ? 'Fleet' : 'Rentals'} at what they have billed per dollar of cost so far (rate sheet hours)` };
        }
        return null;
    };
    if (ready) {
        // The ST / OT lines the sheet can't price yet bill $0 on the Billing
        // tab until their class is mapped; here they count at their cost
        // type's rate.
        (Array.isArray(txns) ? txns : []).forEach((t) => {
            if (!t || t.nb || !t.noRate) return;
            const r = laborRates.noRate[str(t.ct)];
            ctOf(str(t.ct)).noRateEst += pNum(t.cost) * (r != null ? r : 1);
        });
        const lines = [...openLines, ...etcLines];
        const day = moment(today || undefined).format('YYYY-MM-DD');
        const priced = priceToGo(lines.map((l) => ({ ...l, date: day })), priceCtx || {}, fuelCtx || {});
        lines.forEach((l, i) => {
            const p = priced[i];
            const row = l.noCt ? noCts[l.noCt] : ctOf(l.ct);
            const rate = p.nb ? null : rateFor(l.ct);
            let bill = p.bill;
            let { how } = p;
            if (p.nb) bill = 0;
            else if (rate) {
                bill = l.cost * rate.ratio;
                how = rate.how;
                if (l.ct === LABOR_ST && otLift && l.hrs > 0 && l.otHrs > 0 && l.hrsCost) {
                    bill += l.hrsCost * rate.ratio * (l.otHrs / l.hrs) * (otLift - 1);
                    how = `${rate.how}; the forecast's hours past 40 a week at OT (× ${otLift.toFixed(2)})`;
                }
            }
            else if (p.noRate) {
                // Labor with nothing priced and no rate sheet yet.
                const bid = ratioOf(row.billBudget, row.costBudget);
                bill = l.cost * (bid != null ? bid : 1);
                how = bid != null ? 'Bid ratio — no rate sheet yet' : 'At cost — no rate sheet yet';
            }
            row[l.side === 'committed' ? 'billCommitted' : 'billEtc'] += bill;
            addHow(row.toGoHow || (row.toGoHow = []), how || 'At cost', l.cost, bill);
        });
    }

    const classes = CP_CLASSES.map((cls) => {
        const subs = allCts.filter((c) => c.cls === cls).sort((a, b) => a.ct.localeCompare(b.ct));
        const noCt = noCts[cls] ? finish(noCts[cls]) : null;
        const row = rollup([...subs, ...(noCt ? [noCt] : [])], SUM_KEYS, { key: cls, label: CP_LABELS[cls], priced: ready });
        // No plan yet: the open POs are real, the ETC is simply not in.
        row.etc = planReady ? remainingOf(cls) - row.committed : 0;
        row.toGoHow = mergeHows([...subs, ...(noCt ? [noCt] : [])]);
        const out = finish(row);
        out.subRows = subs.map((c) => finish(c));
        out.noCt = noCt;
        return out;
    });

    // Contingency: budget on 101101, the Contingency tab's current amounts by
    // class as the ETC — each at what its class's cost to go bills at (the
    // job's, for a class with none).
    const toGoCost = sumOf(classes, 'committed') + sumOf(classes, 'etc');
    const jobToGoRatio = ready && toGoCost ? sumOf(classes, 'billToGo') / toGoCost : null;
    const contHow = [];
    let contEtc = 0;
    let contBill = 0;
    CP_CLASSES.forEach((cls) => {
        const amt = planReady ? remainingOf(cls, CONTINGENCY_CODE) : 0;
        if (!amt) return;
        contEtc += amt;
        if (!ready) return;
        const c = classes.find((x) => x.key === cls);
        const r = c.toGoRatio != null ? c.toGoRatio : jobToGoRatio;
        const bill = amt * (r != null ? r : 1);
        contBill += bill;
        addHow(contHow, r == null ? 'At cost' : `At ${c.toGoRatio != null ? `${CP_LABELS[cls]}'s` : 'the job\'s'} cost to go (${ratioText(r)})`, amt, bill);
    });
    const contingency = finish({
        ...blank({ key: 'contingency', label: 'Contingency', priced: ready }),
        costBudget: contingencyBudget,
        billBudget: contingencyBillBudget || contingencyBudget * (jobBid != null ? jobBid : 1),
        etc: contEtc, billEtc: contBill, toGoHow: contHow,
    });

    // Non-Billable: the not-billable cost to date, out of the classes, with
    // the budget it came out of (the classes' current budget is that much
    // less). Nothing to go — the plan can't say which cost won't bill, so it
    // stays in the classes' Committed and ETC — so its EAC is what has been
    // spent, on its budget, and it bills nothing. `nbCost` stays on the classes (what
    // came out of each), so here it is 0 and the total counts it once. All
    // of it is the JTD Cost Non-Billable column (`jtdNb`), never invoiced
    // or not invoiced; `closedCost` is what sent summaries closed out at $0.
    const nbJtd = sumOf(allCts, 'nbCost');
    const nbSent = sumOf(allCts, 'nbSent');
    const nonBillable = {
        ...blank({ key: 'nonBillable', label: 'Non-Billable', priced: ready }),
        jtdCost: nbJtd, jtdNb: nbJtd, closedCost: nbSent, curCostBudget: nbJtd, etc: 0, costEac: nbJtd, costDeltaEac: 0, costDeltaBid: nbJtd, sentCost: 0, unsentCost: 0,
        coBill: 0, curBillBudget: 0, billCommitted: 0, billEtc: 0, billToGo: 0, unsentBill: 0, billEac: 0, billDeltaEac: 0, billDeltaBid: 0, margin: -nbJtd,
    };

    // Total Costs foots the classes, the contingency and Non-Billable.
    const rows = [...classes, contingency, nonBillable];
    const BILL_KEYS = ['coBill', 'curBillBudget', 'billCommitted', 'billEtc', 'billToGo', 'unsentBill', 'billEac', 'billDeltaEac', 'billDeltaBid', 'margin'];
    const total = rollup(rows, [...SUM_KEYS, 'jtdNb', 'curCostBudget', 'costEac', 'costDeltaEac', 'costDeltaBid'], { key: 'total', label: 'Total Costs', priced: ready });
    BILL_KEYS.forEach((k) => { total[k] = ready ? sumOf(rows, k) : null; });
    total.bidRatio = jobBid;
    total.toDateRatio = jobToDate;
    total.unbilled = total.jtdBill - total.invoiced;
    total.unsentCost = total.jtdCost - total.sentCost - total.jtdNb;
    const toGo = total.committed + total.etc;
    total.toGoRatio = ready && toGo ? total.billToGo / toGo : null;
    total.toGoHow = ready ? mergeHows(rows) : [];
    total.marginPct = ready ? pctOf(total.margin, total.billEac) : null;

    // Revenue (Billable): the billable side summed — the Contract Amount row.
    // `sentBill` is what has been billed: the lines sent on an invoice, plus
    // what those invoices billed with no line behind it, so it sits against
    // the cost in the JTD Cost Invoiced column; `unsentBill` is the rest of
    // the billable to date, priced. JTD is the two, so JTD + Committed + ETC
    // is the EAC: the lines' dollars (Total Costs) plus the no-line dollars.
    // Non-Billable bills nothing.
    const revenue = {
        bid: total.billBudget, co: ready ? total.coBill : null, current: ready ? total.curBillBudget : null,
        invoiced: ready ? total.invoiced : null, sentBill: ready ? total.sentBill + noLineSent : null,
        unsentBill: ready ? total.unsentBill : null, nb: ready ? 0 : null, noLineSent: ready ? noLineSent : null, committed: ready ? total.billCommitted : null,
        etc: ready ? total.billEtc : null, eac: ready ? total.billEac + noLineSent : null,
        prevPeriod: ready ? billPrevPeriod + noLinePrev : null,
    };
    revenue.jtd = ready ? revenue.sentBill + revenue.unsentBill : null;
    // A job with a revenue budget forecasts revenue at it — the row's Current
    // Budget, so Delta from Budget EAC is 0 (Seth 10/29: "we need eac rev to
    // match budget"); with none, at the contract (Day Zero revenue + approved
    // CO revenue, the Overview's Contract Amount; Seth 10/28 for Aeron's
    // review). The EAC is that figure and the ETC what's left of it after JTD
    // and Committed, so the row still foots. The cost to go as priced stays
    // on the Billable EAC card (`pricedEtc`, `pricedEac`).
    const contract = dayZeroRevenue + coRevenue;
    revenue.tiedTo = !ready ? null : hasRevBudget ? 'budget' : Math.abs(contract) >= 0.005 ? 'contract' : null;
    revenue.contract = revenue.tiedTo === 'budget' ? revenue.current : revenue.tiedTo === 'contract' ? contract : null;
    revenue.pricedEtc = revenue.etc;
    revenue.pricedEac = revenue.eac;
    if (revenue.contract != null) {
        revenue.eac = revenue.contract;
        revenue.etc = revenue.contract - revenue.jtd - revenue.committed;
    }
    revenue.deltaEac = ready ? revenue.eac - revenue.current : null;
    revenue.deltaBid = ready ? revenue.eac - revenue.bid : null;

    // Job Profit: revenue less cost, column by column; JP % on revenue. The
    // to-date columns foot (invoiced + not invoiced + non-billable = JTD),
    // and JTD + Committed + ETC = EAC.
    const diff = (a, b) => (a == null || b == null ? null : a - b);
    const profit = {
        bid: diff(revenue.bid, total.costBudget), co: diff(revenue.co, total.coCost), current: diff(revenue.current, total.curCostBudget),
        jtd: diff(revenue.jtd, total.jtdCost), sent: diff(revenue.sentBill, total.sentCost), unsent: diff(revenue.unsentBill, total.unsentCost),
        nb: diff(revenue.nb, total.jtdNb), committed: diff(revenue.committed, total.committed), etc: diff(revenue.etc, total.etc),
        eac: diff(revenue.eac, total.costEac),
    };
    profit.deltaEac = diff(profit.eac, profit.current);
    profit.deltaBid = diff(profit.eac, profit.bid);
    profit.bidPct = pctOf(profit.bid, revenue.bid);
    profit.currentPct = profit.current == null ? null : pctOf(profit.current, revenue.current);
    ['jtd', 'sent', 'unsent', 'nb', 'committed', 'etc'].forEach((k) => {
        const r = { jtd: revenue.jtd, sent: revenue.sentBill, unsent: revenue.unsentBill, nb: revenue.nb, committed: revenue.committed, etc: revenue.etc }[k];
        profit[`${k}Pct`] = profit[k] == null ? null : pctOf(profit[k], r);
    });
    profit.eacPct = profit.eac == null ? null : pctOf(profit.eac, revenue.eac);
    profit.deltaEacPct = profit.eacPct == null || profit.currentPct == null ? null : profit.eacPct - profit.currentPct;
    profit.deltaBidPct = profit.eacPct == null || profit.bidPct == null ? null : profit.eacPct - profit.bidPct;

    // OH Allocation: the Day Zero's 60900 as the budget, 60900 cost to
    // date, and the rest of the billable at the job's overhead % — the Bid
    // Forecast's rule, on billable instead of contract.
    const ohPct = pNum(ohPercent || 0) / 100;
    const oh = {
        bid: ohBudget, co: 0, current: ohBudget, jtd: jtdOh,
        etc: ready && revenue.eac != null ? (revenue.eac - revenue.prevPeriod) * ohPct : null,
    };
    oh.eac = oh.etc == null ? null : oh.jtd + oh.etc;
    oh.deltaEac = diff(oh.eac, oh.current);
    oh.deltaBid = diff(oh.eac, oh.bid);

    // Gross Margin: job profit less overhead; GM % on revenue.
    const gm = {
        bid: diff(profit.bid, oh.bid), current: diff(profit.current, oh.current), eac: diff(profit.eac, oh.eac),
    };
    gm.deltaEac = diff(gm.eac, gm.current);
    gm.deltaBid = diff(gm.eac, gm.bid);
    gm.bidPct = pctOf(gm.bid, revenue.bid);
    gm.currentPct = gm.current == null ? null : pctOf(gm.current, revenue.current);
    gm.eacPct = gm.eac == null ? null : pctOf(gm.eac, revenue.eac);
    gm.deltaEacPct = gm.eacPct == null || gm.currentPct == null ? null : gm.eacPct - gm.currentPct;
    gm.deltaBidPct = gm.eacPct == null || gm.bidPct == null ? null : gm.eacPct - gm.bidPct;

    // Not-to-exceed: headroom against the billable EAC, how much is used,
    // and — at the last RUN_RATE_WEEKS complete weeks' billable — when the
    // NTE would be reached if that pace held.
    const nteAmt = pNum(nte || 0);
    const now = moment(today || undefined);
    const thisWeek = now.clone().startOf('isoWeek');
    const window = [];
    for (let i = 1; i <= RUN_RATE_WEEKS; i += 1) window.push(thisWeek.clone().subtract(i, 'weeks').format('YYYY-MM-DD'));
    const windowAmt = window.reduce((a, k) => a + (weeks[k] || 0), 0);
    const runRate = ready && window.some((k) => weeks[k]) ? windowAmt / RUN_RATE_WEEKS : null;
    const leftToNte = nteAmt > 0 && ready ? nteAmt - total.jtdBill : null;
    const weeksToNte = leftToNte != null && leftToNte > 0 && runRate > 0 ? leftToNte / runRate : null;
    const nteCard = {
        amount: nteAmt,
        headroom: nteAmt > 0 && ready ? nteAmt - revenue.eac : null,
        headroomPct: nteAmt > 0 && ready ? (nteAmt - revenue.eac) / nteAmt : null,
        consumedPct: nteAmt > 0 && ready ? total.jtdBill / nteAmt : null,
        runRate,
        runRateWeeks: RUN_RATE_WEEKS,
        weeksToNte,
        reachedOn: weeksToNte != null ? thisWeek.clone().add(Math.ceil(weeksToNte * 7), 'days').format('YYYY-MM-DD') : null,
    };

    // The Billings card, as the Bid Forecast has it: earned = the grid's
    // revenue to date (no-line dollars included), billings = JDE.
    const jde = pNum(jdeBilled || 0);
    const billing = {
        prevPeriod: revenue.prevPeriod,
        jtdBill: revenue.jtd,
        invoiced: ready ? total.invoiced : null,
        unbilled: ready ? total.unbilled : null,
        nbCost: ready ? total.nbCost : null,
        noRate: ready ? total.noRate : 0,
        jdeBilled: jde,
        overUnder: ready ? jde - revenue.jtd : null,
        leftToBill: ready ? revenue.eac - jde : null,
    };
    const labor = classes.find((c) => c.key === 'Labor');
    const pct = {
        cost: pctOf(total.jtdCost, total.costEac),
        labor: labor ? pctOf(labor.jtdCost, labor.costEac) : null,
        bill: ready ? pctOf(revenue.jtd, revenue.eac) : null,
    };
    const laborBill = ready && labor ? laborBillOf(laborParts, labor) : null;

    return {
        ready, planReady, hasRevBudget, classes, contingency, nonBillable, total, revenue, profit, oh, gm, nte: nteCard, billing, pct, laborBill, laborRates,
        coRevenue, dayZeroRevenue,
        weeks: Object.keys(weeks).sort().map((k) => ({ week: k, amount: weeks[k] })),
    };
}

// Display helpers shared by the tab and its tests.
export const ratioText = (r) => (r == null || !Number.isFinite(r) ? '–' : `${r.toFixed(2)}×`);
export const pctText = (p, digits = 1) => (p == null || !Number.isFinite(p) ? '–' : `${(p * 100).toFixed(digits)}%`);
// How a row's cost to go bills, in a line: its one method, or each method
// with its share of the cost.
// A method under half a percent of the cost is left out.
export const howText = (list) => {
    const hs = (list || []).filter((h) => Math.round(h.cost));
    if (hs.length <= 1) return hs.length ? hs[0].how : '';
    const all = hs.reduce((a, h) => a + Math.abs(h.cost), 0);
    const shown = hs.filter((h) => !all || Math.abs(h.cost) / all >= 0.005);
    if (shown.length === 1) return shown[0].how;
    return shown.map((h) => `${h.how} (${pctText(all ? Math.abs(h.cost) / all : 0, 0)})`).join('; ');
};
